import { Page, PageTitle } from "@/components/page"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { zodResolver } from "@hookform/resolvers/zod"
import { User, Lock, Eye, EyeOff, Shield, Trash2 } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { useState, useEffect } from "react"
import { supabase } from "@/lib/data"
import { toast } from "sonner"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { useNavigate } from "react-router-dom"
import { useUser } from "@/lib/hooks/useUser"

export const profile = {
  title: "Profile",
  description: "Manage your account settings and security.",
  route: "/settings/profile",
  url: "/settings/profile",
  icon: User,
  component,
  group: "settings",
} satisfies PageConfig

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z.string().min(8, "Password must be at least 8 characters"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
})

type PasswordFormData = z.infer<typeof passwordSchema>

interface Factor {
  id: string
  factor_type: string
  friendly_name?: string
  phone?: string
  status: string
}

function component() {
  const { data: user } = useUser()
  const navigate = useNavigate()
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [factors, setFactors] = useState<Factor[]>([])
  const [lastSignInAt, setLastSignInAt] = useState<string | null>(null)
  const [loadingFactors, setLoadingFactors] = useState(true)
  const [unenrollingId, setUnenrollingId] = useState<string | null>(null)

  const form = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  })

  useEffect(() => {
    loadFactors()
  }, [])

  const loadFactors = async () => {
    setLoadingFactors(true)
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser()

      if (userError) {
        toast.error(userError.message || "Failed to load user")
      } else {
        setLastSignInAt(userData.user?.last_sign_in_at || null)
      }

      const { data, error } = await supabase.auth.mfa.listFactors()

      if (error) {
        toast.error(error.message || "Failed to load factors")
        return
      }

      const allFactors = [...(data.totp || []), ...(data.phone || [])]
      setFactors(allFactors)
    } catch (error) {
      toast.error("An unexpected error occurred")
    } finally {
      setLoadingFactors(false)
    }
  }

  const handleUnenroll = async (factorId: string) => {
    setUnenrollingId(factorId)
    try {
      const { error } = await supabase.auth.mfa.unenroll({
        factorId,
      })

      if (error) {
        toast.error(error.message || "Failed to remove factor")
        return
      }

      toast.success("Two-factor authentication removed successfully")
      await supabase.auth.refreshSession()
      loadFactors()
    } catch (error) {
      toast.error("An unexpected error occurred")
    } finally {
      setUnenrollingId(null)
    }
  }

  async function onSubmit(data: PasswordFormData) {
    setIsLoading(true)

    try {
      const { data: { user } } = await supabase.auth.getUser()

      if (!user) {
        toast.error("You must be logged in to change your password")
        return
      }

      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email!,
        password: data.currentPassword,
      })

      if (signInError) {
        toast.error("Current password is incorrect")
        form.setError("currentPassword", { message: "Current password is incorrect" })
        return
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: data.newPassword
      })

      if (updateError) {
        toast.error(updateError.message || "Failed to update password")
        return
      }

      toast.success("Password updated successfully")
      form.reset()
    } catch (error) {
      toast.error("An unexpected error occurred")
    } finally {
      setIsLoading(false)
    }
  }

  const verifiedFactors = factors.filter((f) => f.status === "verified")

  return (
    <Page>
      <div className="pb-8">
        <PageTitle />

        <div className="rgrid">
          <div className="row">
            <div className="rc-12">
              <Card>
                <CardHeader>
                  <CardTitle>Account Information</CardTitle>
                  <CardDescription>
                    Your account details and profile information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-gray-500">Name</div>
                      <div className="text-base">
                        {user?.first_name && user?.last_name
                          ? `${user.first_name} ${user.last_name}`
                          : user?.email || "N/A"}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Email</div>
                      <div className="text-base">{user?.email || "N/A"}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Role</div>
                      <div className="text-base">{user?.role_name || "No role assigned"}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Status</div>
                      <div className="text-base">{user?.status || "N/A"}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Last Sign In</div>
                      <div className="text-base">
                        {lastSignInAt ? new Date(lastSignInAt).toLocaleString() : "N/A"}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="row">
            <div className="rc-12">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="h-5 w-5" />
                    Change Password
                  </CardTitle>
                  <CardDescription>
                    Update your account password to keep it secure
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="currentPassword"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Current Password</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <Input
                                    {...field}
                                    type={showCurrentPassword ? "text" : "password"}
                                    placeholder="Enter your current password"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                  >
                                    {showCurrentPassword ? (
                                      <EyeOff className="h-4 w-4 text-gray-500" />
                                    ) : (
                                      <Eye className="h-4 w-4 text-gray-500" />
                                    )}
                                  </Button>
                                </div>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="newPassword"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>New Password</FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <Input
                                      {...field}
                                      type={showNewPassword ? "text" : "password"}
                                      placeholder="Enter your new password"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                      onClick={() => setShowNewPassword(!showNewPassword)}
                                    >
                                      {showNewPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-500" />
                                      ) : (
                                        <Eye className="h-4 w-4 text-gray-500" />
                                      )}
                                    </Button>
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="confirmPassword"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Confirm New Password</FormLabel>
                                <FormControl>
                                  <div className="relative">
                                    <Input
                                      {...field}
                                      type={showConfirmPassword ? "text" : "password"}
                                      placeholder="Confirm your new password"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    >
                                      {showConfirmPassword ? (
                                        <EyeOff className="h-4 w-4 text-gray-500" />
                                      ) : (
                                        <Eye className="h-4 w-4 text-gray-500" />
                                      )}
                                    </Button>
                                  </div>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="flex justify-end">
                          <Button type="submit" disabled={isLoading}>
                            {isLoading ? "Updating..." : "Update Password"}
                          </Button>
                        </div>
                      </div>
                    </form>
                  </Form>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="row">
            <div className="rc-12">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Two-Factor Authentication
                  </CardTitle>
                  <CardDescription>
                    {verifiedFactors.length > 0
                      ? "Two-factor authentication is enabled on your account"
                      : "Add an extra layer of security to your account"}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingFactors ? (
                    <div className="text-center py-4">Loading...</div>
                  ) : verifiedFactors.length === 0 ? (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">
                        Enable two-factor authentication to add an extra layer of security to your account.
                      </p>
                      <Button onClick={() => navigate("/settings/mfa/enroll")}>
                        Enable 2FA
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <h3 className="font-semibold">Active Factors</h3>
                        {verifiedFactors.map((factor) => (
                          <div
                            key={factor.id}
                            className="flex items-center justify-between p-4 border rounded-lg"
                          >
                            <div className="space-y-1">
                              <div className="font-medium">
                                {factor.friendly_name || factor.factor_type.toUpperCase()}
                              </div>
                              {factor.phone && (
                                <div className="text-sm text-gray-500">{factor.phone}</div>
                              )}
                              <div className="text-xs text-gray-400">
                                Type: {factor.factor_type === "totp" ? "Authenticator App" : "Phone"}
                              </div>
                            </div>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  disabled={unenrollingId === factor.id}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  {unenrollingId === factor.id ? "Removing..." : "Remove"}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remove Two-Factor Authentication?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to remove this factor? Your account will be less secure.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleUnenroll(factor.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Remove
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => navigate("/settings/mfa/enroll")}
                      >
                        Add Another Factor
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Page>
  )
}

